import getColor from '~/lib/getColor'

export const THREE_D = '3D'
export const TWO_D = '2D'
export const IM_DFM = 'im-dfm'
export const GEO_JSON = 'GEO_JSON'
export const PDF_JSON = 'PDF_JSON'
export const SCENE_BUNDLE_PERSPECTIVE = 'SCENE_BUNDLE'

export const MODE_QUERY_PARAM = 'mode'
export const SLIDE_QUERY_PARAM = 'slide'
export const VIEWER_QUERY_PARAM = 'viewer'

export const THREE_D_VIEWER_QUERY_PARAM = '3d'
export const PDF_VIEWER_QUERY_PARAM = 'pdf'

export const DFM_SOURCE_TYPE_3D = '3d'

export const NO_DATA = '—'

export const MODEL_VIEWER_PANEL_PLACEMENT = 'admin-quoting-viewer'
export const PLACEMENT = 'part-dfm-report'

export const ACTIONS = 'actions'
export const CUSTOMER_UPLOAD = 'Uploaded by customer'
export const DELETE_DFM_REPORT = 'delete-dfm-report'
export const FICTIV_UPLOAD = 'Uploaded by Fictiv'
export const SOURCE_FICTIV = 'Fictiv'

export const DEFAULT_ADVANCED_INPECTION_MARKUP_PERCENTAGE = 43
export const FILE_MAX_LENGTH = 38

export const BORDER = `1px solid ${getColor('blackAlpha09')}`

export const DFM_REPORT_BY_FICTIV_ENGINEER =
    'The DFM report is prepared by a Fictiv engineer after reviewing your parts and configurations.'

export const RACE_CONDITION_ERR_MSG =
    'Another user has saved pricing parameters and you are going to over ride it'

export const RACE_CONDITION_MODAL_TITLE =
    'Are you sure you want to overwrite the existing pricing parameters?'

export const RACE_CONDITION_MODAL_BODY = `The pricing parameters have previously been modified
    and by continuing, you will be overwriting this
    information. This action cannot be undone. Are you
    sure you want to continue?`

export const PASS_AND_RELEASE_ERROR = 'ERROR_IN_PASS_AND_RELEASE'

export const UNSAVED_DFM_SLIDES_MODAL_MESSAGE = 'Discard unsaved 3D slide(s)?'

export const OPERATION_REGION_USA = 'usa'
export const OPERATION_REGION_CHINA = 'china'
export const OPERATION_REGION_INDIA = 'india'

/* Dfm tags */
export const DFM_TAGS = {
    MILL: 'MILL',
    LATHE: 'LATHE',
    LIVE_TOOLING_LATHE: 'LIVE_TOOLING_LATHE',
    FINISHING: 'FINISHING',
    HARDWARE_INSTALLATION: 'HARDWARE_INSTALLATION',
    FOUR_AXIS: 'FOUR_AXIS',
    FIVE_AXIS: 'FIVE_AXIS',
    FIVE_AXIS_MILL: 'FIVE_AXIS_LATHE',
    FIVE_AXIS_LATHE: 'FIVE_AXIS_MILL',
    TIGHT_TOLERANCES: 'TIGHT_TOLERANCES',
    NON_STANDARD_MATERIAL: 'NON_STANDARD_MATERIAL',
    STOCK_THICKNESS_APPROVED: 'STOCK_THICKNESS_APPROVED',
    REVISION: 'REVISION',
    THREE_D_MACHINING: 'THREE_D_MACHINING',
    ENGRAVING: 'ENGRAVING',
    OTS_REWORK: 'OTS_REWORK',
    AUTO: 'AUTO',
    SPECIAL_TOOLING: 'SPECIAL_TOOLING',
    UNDERCUTS: 'UNDERCUTS',
    EDM: 'EDM',
    HOBBING: 'HOBBING',
    GUN_DRILLING: 'GUN_DRILLING',
} as const

export const DFM_TAGS_MAP = Object.freeze({
    [DFM_TAGS.MILL]: 'Mill',
    [DFM_TAGS.LATHE]: 'Lathe',
    [DFM_TAGS.LIVE_TOOLING_LATHE]: 'Live tooling lathe',
    [DFM_TAGS.FINISHING]: 'Finishing',
    [DFM_TAGS.HARDWARE_INSTALLATION]: 'Hardware installation',
    [DFM_TAGS.FOUR_AXIS]: '4-axis',
    [DFM_TAGS.FIVE_AXIS]: '5-axis',
    [DFM_TAGS.FIVE_AXIS_LATHE]: '5-axis Lathe',
    [DFM_TAGS.FIVE_AXIS_MILL]: '5-axis Mill',
    [DFM_TAGS.TIGHT_TOLERANCES]: 'Tight tolerances',
    [DFM_TAGS.NON_STANDARD_MATERIAL]: 'Non standard material',
    [DFM_TAGS.STOCK_THICKNESS_APPROVED]: 'Stock thickness approved',
    [DFM_TAGS.REVISION]: 'Revision',
    [DFM_TAGS.THREE_D_MACHINING]: '3D machining',
    [DFM_TAGS.ENGRAVING]: 'Engraving',
    [DFM_TAGS.OTS_REWORK]: 'OTS rework',
    [DFM_TAGS.AUTO]: 'Autoquoted',
    [DFM_TAGS.SPECIAL_TOOLING]: 'Special tooling',
    [DFM_TAGS.UNDERCUTS]: 'Undercuts',
    [DFM_TAGS.EDM]: 'EDM',
    [DFM_TAGS.HOBBING]: 'Hobbing',
    [DFM_TAGS.GUN_DRILLING]: 'Gun drilling',
} as const)

/* Quoting form keys */
export const ADVANCED_INSPECTION_FORM_KEYS = {
    ADVANCED_INSPECTION_NOTES: 'advancedInspectionNotes',
    ADVANCED_INSPECTION_NUM_POINTS: 'advancedInspectionNumPoints',
    ADVANCED_INSPECTION_NUM_SPECIAL_POINTS:
        'advancedInspectionNumSpecialPoints',
    ADVANCED_INSPECTION_PERCENT_MARKUP: 'advancedInspectionPercentMarkup',
    ADVANCED_INSPECTION_DOMESTIC_LEAD_TIME_DAYS:
        'advancedInspectionDomesticLeadTimeDays',
    ADVANCED_INSPECTION_OVERSEAS_LEAD_TIME_DAYS:
        'advancedInspectionOverseasLeadTimeDays',
    ADVANCED_INSPECTION_MP_COST: 'advancedInspectionMpCost',
} as const

const GEOMETRY_FORM_KEYS = {
    NUM_SETUPS: 'numSetups',
    NUM_NON_STANDARD_FIXTURES: 'numNonStandardFixtures',
    CAM_SECONDS: 'camSeconds',
} as const

const MATERIAL_FORM_KEYS = {
    DOMESTIC_MATERIAL_COST: 'materialCostInCents',
    CHINA_MATERIAL_COST: 'chinaMaterialCostInCents',
    INDIA_MATERIAL_COST: 'indiaMaterialCostInCents',
    SCRAP_MATERIAL: 'scrapMaterial',
    TOOL_COSTS: 'toolCosts',
    OVERSEAS_TOOL_COSTS: 'overseasToolCosts',
} as const

const TOOLING_FORM_KEYS = {
    DOMESTIC_UNDERCUTTING_COST: 'domesticUndercuttingCosts',
    DOMESTIC_REAMER_COST: 'domesticReamerCosts',
    OVERSEAS_UNDERCUTTING_COST: 'overseasUndercuttingCosts',
    OVERSEAS_REAMER_COST: 'overseasReamerCosts',
    DOMESTIC_TAP_TOOL_COSTS: 'domesticTapToolCosts',
    OVERSEAS_TAP_TOOL_COSTS: 'overseasTapToolCosts',
} as const

const ADDITIONAL_COST_FORM_KEYS = {
    NUM_TIGHT_TOLERANCES: 'numTightTolerances',
    NUM_VERY_TIGHT_TOLERANCES: 'numVeryTightTolerances',
    MISC_NRE: 'miscNonRecurringEngineeringCostInCents',
    NUM_THREADED_FEATURES: 'numThreadedFeatures',
    MISC_PRODUCTION_COST: 'miscProductionCostInCents',
    LEGACY_MISC_PRODUCTION_COST: 'legacyMiscProdCosts',
} as const

const SUB_PROCESS_FORM_KEYS = {
    HOBBING_NRE: 'hobbingNonRecurringEngineeringCost',
    HOBBING_PRODUCTION_COST: 'hobbingProductionCosts',
    DEEP_HOPE_NRE: 'deepHoleNonRecurringEngineeringCost',
    DEEP_HOPE_PRODUCTION_COST: 'deepHoleProductionCosts',
    CLASS_A_REQUIRED: 'classARequired',
    CLASS_A_PART_COMPLEXITY: 'classAPartComplexity',
    CLASS_A_NRE: 'classANonRecurringEngineeringCost',
    CLASS_A_PRODUCTION_COST: 'classAProductionCosts',
} as const

const EDM_CALCULATOR_FORM_KEYS = {
    EDM_PRODUCTION_COSTS: 'edmProductionCosts',
    CHINA_EDM_PRODUCTION_COSTS: 'chinaEdmProductionCosts',
    INDIA_EDM_PRODUCTION_COSTS: 'indiaEdmProductionCosts',
}

const MACHINING_FORM_KEYS = {
    MACHINING_SECONDS: 'machiningSeconds',
    MACHINE_TIME_MULTIPLIER: 'machineTimeMultiplier',
    TABBING_MACHINING_SECONDS: 'tabbingMachiningSeconds',
    INCREMENTAL_SETUP_TIME: 'incrementalSetupTime',
} as const

const SCRAP_RATE_FORM_KEYS = {
    SCRAP_RATE_NUM_PARTS: 'scrapRateNumParts',
    SCRAP_RATE_NUM_PARTS_BASE: 'scrapRateNumPartsBase',
} as const

export const FORM_KEY_PREFIXES = {
    LEAD_TIME_OVERRIDE: 'lead-time-override-',
    MARKUP_OVERRIDE: 'markup-override-',
    REGION_SCALING_FACTORS: 'region-scaling-factors-',
} as const

export const QUANTITY_RANGE_FORM_KEYS = {
    QUANTITY_RANGE: 'quantityRange',
    QUANTITY_LOWER_RANGE: 'quantityLowerRange',
    QUANTITY_UPPER_RANGE: 'quantityUpperRange',
    IS_QUANTITY_RANGE_UNAVAILABLE: 'isQuantityRangeUnavailable',
} as const

export const QUOTING_FORM_KEYS = {
    PENDING_QUANTITY: 'pendingQuantity',
    PENDING_MATERIAL: 'pendingMaterial',
    PENDING_LEAD_TIME: 'pendingLeadTime',
    INDIA_PENDING_LEAD_TIME: 'indiaPendingLeadTime',
    ALLOWED_PRIORITY_IDS: 'allowedPriorityIds',
    ALLOWED_REGIONS: 'allowedRegions',
    DFM_TAGS: 'dfmTags',
    NOTES: 'notes',
    ...ADVANCED_INSPECTION_FORM_KEYS,
    ...GEOMETRY_FORM_KEYS,
    ...MATERIAL_FORM_KEYS,
    ...TOOLING_FORM_KEYS,
    ...ADDITIONAL_COST_FORM_KEYS,
    ...SUB_PROCESS_FORM_KEYS,
    ...EDM_CALCULATOR_FORM_KEYS,
    ...MACHINING_FORM_KEYS,
    ...SCRAP_RATE_FORM_KEYS,
    ...FORM_KEY_PREFIXES,
    ...QUANTITY_RANGE_FORM_KEYS,
} as const

export type QuotingFormKeysType =
    typeof QUOTING_FORM_KEYS[keyof typeof QUOTING_FORM_KEYS]
